import { LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { apiClient, type BulkTarget, type SelectionBreakdown } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { Panel } from "@/shared/components/charts/Panel";
import { RankedBars } from "@/shared/components/charts/RankedBars";
import {
  approachChips,
  barValue,
  describeThemeSources,
  formatCount,
  polarityChips,
  type CountChip
} from "./selection-breakdown";

/** Quantos temas/mensagens aparecem de início; "ver todos" abre o resto
 *  (o servidor devolve no máximo 100). */
const INITIAL_BARS = 15;

export type SelectionBreakdownPanelProps = {
  token: string;
  /** O alvo da seleção, o MESMO que o lote mandaria. Lido no clique. */
  target: () => BulkTarget;
  /** Muda quando o conjunto selecionado muda (`selectionKey`). A contagem
   *  na tela fica "desatualizada" — nunca se refaz sozinha, porque cada
   *  consulta é um passe por até 50 mil menções. */
  selectionKey: string;
};

type Loaded = { data: SelectionBreakdown; key: string };

/**
 * O "Resumo da seleção" (2026-10-20): quantas vezes cada tema e cada
 * mensagem-chave aparecem nas menções selecionadas — o "repetido demais"
 * que a analista procurava baixando a planilha. Sem IA: é contagem.
 *
 * Sob demanda (botão "Ver contagem"), sem polling, e sem recarregar sozinho
 * quando a seleção muda. Resposta velha nunca vence a nova (descarte por
 * CARGA, e a consulta anterior é abortada). O botão NÃO trava enquanto
 * carrega, de propósito: pedir de novo é "esquece a anterior", e é isso
 * que o descarte por carga cobre.
 */
export function SelectionBreakdownPanel({ token, target, selectionKey }: SelectionBreakdownPanelProps) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const run = useRef(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => () => controller.current?.abort(), []);

  const load = async () => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const mine = ++run.current;
    const keyAtClick = selectionKey;
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.selectionBreakdown(token, target(), { signal: abort.signal });
      if (mine !== run.current) return;
      setLoaded({ data, key: keyAtClick });
      setShowAllThemes(false);
      setShowAllMessages(false);
    } catch (failure) {
      if (mine !== run.current) return;
      if ((failure as { name?: string } | null)?.name === "AbortError") return;
      setError(errorMessage(failure, "Não foi possível contar a seleção. Tente de novo."));
    } finally {
      if (mine === run.current) setLoading(false);
    }
  };

  const stale = loaded !== null && loaded.key !== selectionKey;

  return (
    <Panel
      title="Resumo da seleção"
      subtitle="Quantas vezes cada subtema e mensagem-chave aparecem nas menções selecionadas."
      className={`selection-breakdown${stale ? " is-stale" : ""}`}
      action={
        <button
          type="button"
          className="secondary-button"
          onClick={() => void load()}
        >
          {loading && <LoaderCircle size={15} className="spin" aria-hidden="true" />}
          {loaded ? "Contar de novo" : "Ver contagem"}
        </button>
      }
    >
      {error && (
        <p className="selection-breakdown-error" role="alert">
          {error}
        </p>
      )}
      {!loaded && !error && (
        <p className="selection-breakdown-note">
          Clique em &quot;Ver contagem&quot; para somar os subtemas e as mensagens-chave da seleção.
        </p>
      )}
      {loaded && (
        <>
          {stale && (
            <p className="selection-breakdown-stale" role="status">
              A seleção mudou — esta contagem está desatualizada. Clique em &quot;Contar de novo&quot;.
            </p>
          )}
          <BreakdownBody
            data={loaded.data}
            showAllThemes={showAllThemes}
            showAllMessages={showAllMessages}
            onShowAllThemes={() => setShowAllThemes(true)}
            onShowAllMessages={() => setShowAllMessages(true)}
          />
        </>
      )}
    </Panel>
  );
}

function BreakdownBody({
  data,
  showAllThemes,
  showAllMessages,
  onShowAllThemes,
  onShowAllMessages
}: {
  data: SelectionBreakdown;
  showAllThemes: boolean;
  showAllMessages: boolean;
  onShowAllThemes: () => void;
  onShowAllMessages: () => void;
}) {
  const sources = describeThemeSources(data.theme_sources);
  const themeItems = data.themes.map(theme => ({
    key: theme.name,
    label: theme.group_name ? `${theme.group_name} · ${theme.name}` : theme.name,
    value: theme.count
  }));
  const messageItems = data.key_messages.map(message => ({
    key: message.name,
    label: message.name,
    value: message.count
  }));
  return (
    <>
      <p className="selection-breakdown-note">
        <strong>{formatCount(data.total)}</strong> {data.total === 1 ? "menção" : "menções"} na seleção.
      </p>
      <div className="selection-breakdown-grid">
        <div className="selection-breakdown-block">
          <h3 className="selection-breakdown-title">Subtemas</h3>
          <BarsOrEmpty
            items={themeItems}
            total={data.total}
            noun="menções"
            empty="Nenhum subtema nas menções selecionadas."
            expanded={showAllThemes}
            onExpand={onShowAllThemes}
          />
          {data.themes_truncated && (
            <p className="selection-breakdown-sources">
              Há mais subtemas do que cabem aqui — mostrando os {formatCount(data.themes.length)} mais frequentes.
            </p>
          )}
          {sources && (
            <p className="selection-breakdown-sources">
              Origem dos subtemas (ligações menção × subtema): {sources}.
            </p>
          )}
        </div>
        <div className="selection-breakdown-block">
          <h3 className="selection-breakdown-title">Mensagens-chave</h3>
          <BarsOrEmpty
            items={messageItems}
            total={data.total}
            noun="menções"
            empty="Nenhuma mensagem-chave nas menções selecionadas."
            expanded={showAllMessages}
            onExpand={onShowAllMessages}
          />
          {data.key_messages_truncated && (
            <p className="selection-breakdown-sources">
              Há mais mensagens do que cabem aqui — mostrando as {formatCount(data.key_messages.length)} mais frequentes.
            </p>
          )}
        </div>
        <div className="selection-breakdown-block">
          <h3 className="selection-breakdown-title">Polaridade</h3>
          <Chips chips={polarityChips(data)} />
        </div>
        <div className="selection-breakdown-block">
          <h3 className="selection-breakdown-title">Abordagem</h3>
          <Chips chips={approachChips(data)} />
        </div>
      </div>
    </>
  );
}

function BarsOrEmpty({
  items,
  total,
  noun,
  empty,
  expanded,
  onExpand
}: {
  items: Array<{ key: string; label: string; value: number }>;
  total: number;
  noun: string;
  empty: string;
  expanded: boolean;
  onExpand: () => void;
}) {
  if (items.length === 0) return <p className="selection-breakdown-note">{empty}</p>;
  return (
    <>
      <RankedBars
        items={items}
        noun={noun}
        limit={expanded ? items.length : INITIAL_BARS}
        showShare={false}
        formatValue={value => barValue(value, total)}
      />
      {!expanded && items.length > INITIAL_BARS && (
        <button type="button" className="text-button selection-breakdown-more" onClick={onExpand}>
          Ver todos ({formatCount(items.length)})
        </button>
      )}
    </>
  );
}

function Chips({ chips }: { chips: CountChip[] }) {
  if (chips.length === 0) return <p className="selection-breakdown-note">Sem dados.</p>;
  return (
    <ul className="selection-breakdown-chips">
      {chips.map(chip => (
        <li key={chip.key}>
          {chip.label}: <strong>{formatCount(chip.count)}</strong>
        </li>
      ))}
    </ul>
  );
}
